import React from "react";
import { fileUrl } from "@/lib/api";
import { useLang } from "@/lib/i18n";

/**
 * Print-only header for shop reports / KPI pages.
 *
 * Hidden on screen (`hidden print:block`) and injected at the top of each
 * report content area. Shows the shop logo + name + address, the report
 * title, optional date range, and a printed-at timestamp — so a printed
 * sheet (or Save-as-PDF) carries full context without the website chrome.
 */
export function ReportPrintHeader({ shop, title, range }) {
  const { lang } = useLang();
  const now = new Date();
  const printedOn = now.toLocaleString(lang === "th" ? "th-TH" : "en-GB", {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
  const address = [shop?.address, shop?.area, shop?.town].filter(Boolean).join(", ");
  return (
    <div
      className="hidden print:block print-sheet-header mb-5 pb-4 border-b-2 border-stone-800"
      data-testid="report-print-header"
    >
      <div className="flex items-center gap-4">
        {shop?.logo_path && (
          <img
            src={fileUrl(shop.logo_path)}
            alt={shop?.name || "Shop logo"}
            className="w-20 h-20 object-contain rounded ring-1 ring-stone-300"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-2xl font-serif text-stone-900 leading-tight">{shop?.name || "Shop"}</div>
          {address && <div className="text-xs text-stone-600 mt-0.5">{address}</div>}
          <div className="text-[11px] uppercase tracking-widest text-stone-500 mt-1 font-mono">
            Management Information System
          </div>
        </div>
        <div className="text-right">
          <div className="font-serif text-xl text-emerald-900">{title}</div>
          {range && <div className="text-xs text-stone-600 mt-0.5 font-mono">{range}</div>}
          <div className="text-[10px] text-stone-500 mt-1 font-mono">Printed {printedOn}</div>
        </div>
      </div>
    </div>
  );
}
